/**
 * Store API functions for the GDB Cloud Edition.
 *
 * Calls the existing Rust/Axum endpoints:
 *   GET  /api/products         -> ProductsResponse
 *   POST /api/stripe/checkout  -> CheckoutResponse
 *
 * Store + commerce are CLOUD-hosted (like auth) — the local sidecar does NOT
 * mount /api/products or /api/stripe/*. So these calls go through authClient
 * (cloud base + Bearer), NOT the sidecar `api` client. Routing them at the
 * sidecar (apiBase) inside the plugin caused a CORS failure on /api/products
 * and the "Could not load checkout" error.
 */

import { authClient } from '@/lib/api-client'
import type {
  CheckoutRequest,
  CheckoutResponse,
  ProductDetailResponse,
  ProductsResponse,
} from './types'

/**
 * Fetch the full product catalog.
 * GET /api/products (public, no auth required).
 */
export async function getProducts(): Promise<ProductsResponse> {
  const response = await authClient.get('/api/products')
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to load products')
  }
  return response.json() as Promise<ProductsResponse>
}

/**
 * Fetch a single product by slug — the /modules/:slug detail page surface.
 * GET /api/products/:slug (public, no auth required).
 *
 * Throws "Product not found" on 404 so the caller's React Query `error`
 * branch can render a 404 view distinguishable from generic load failures.
 */
export async function getProductBySlug(
  slug: string,
): Promise<ProductDetailResponse> {
  const response = await authClient.get(`/api/products/${encodeURIComponent(slug)}`)
  if (response.status === 404) {
    throw new Error('Product not found')
  }
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to load product')
  }
  return response.json() as Promise<ProductDetailResponse>
}

/**
 * Initiate a Stripe checkout session for a product.
 * POST /api/stripe/checkout (requires Bearer JWT).
 */
export async function checkout(request: CheckoutRequest): Promise<CheckoutResponse> {
  const response = await authClient.post('/api/stripe/checkout', request)
  if (!response.ok) {
    const data: { error?: string } = await response.json().catch(() => ({}))
    throw new Error(data.error || 'Checkout failed')
  }
  return response.json() as Promise<CheckoutResponse>
}
